import type { DateRange } from "@daypicker/react"
import { billableHireDays, calculateHireCharge, calculateWeeklyHirePence } from "../../../supabase/functions/_shared/hire-rates.ts"
import { normaliseGbPostcode } from "../../../supabase/functions/_shared/postcode.ts"
import { localDate, toDateOnly } from "./format"
import type { BookingSelection, CalendarAvailability, Product, RangeChoice } from "./types"

export const CALENDAR_FRESH_MS = 30_000

export const POSTCODE_DEBOUNCE_MS = 250

export function calendarPostcode(value: string): string {
    try { return normaliseGbPostcode(value).postcode } catch { return "" }
}

export const POSTCODE_VALIDATION_MESSAGE = "Please enter a valid postcode"
export const MINIMUM_HIRE_MESSAGE = "At least one full day of hire is required."

export function dayDifference(from: Date, to: Date) {
    return Math.round((Date.UTC(to.getFullYear(), to.getMonth(), to.getDate()) - Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())) / 86_400_000)
}

export function isRequiredHireDay(day: Date, start: Date | undefined, product: Product | null) {
    return product?.productGroup === "dehumidifier" && Boolean(start && dayDifference(start, day) === 1)
}

function minimumBookingDays(product: Product | null) {
    return Math.max(product?.minimumBookingDays ?? 2, product?.productGroup === "dehumidifier" ? 3 : 2)
}

export function serviceRangeError(range: DateRange | undefined, calendar: CalendarAvailability | null, existingCheckout: boolean) {
    if (!calendar || !range?.from) return ""
    if (calendar.deliveryUnavailableDates.includes(toDateOnly(range.from))) return "Your delivery date is no longer available. Choose new dates."
    if (range.to && calendar.collectionUnavailableDates.includes(toDateOnly(range.to))) return "Your collection date is no longer available. Choose new dates."
    if (!existingCheckout && (toDateOnly(range.from) < calendar.noticeDate || calendar.deliveryNoticeUnavailableDates.includes(toDateOnly(range.from)))) {
        return "Your delivery date is no longer available for this postcode or booking time. Choose new dates."
    }
    return ""
}

export function validateCalendarAvailability(value: CalendarAvailability): CalendarAvailability {
    const validDates = (dates: unknown): dates is string[] => Array.isArray(dates)
        && dates.every((date) => typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date) && toDateOnly(localDate(date)) === date)
    if (!value || !validDates(value.unavailableDates) || !validDates(value.deliveryUnavailableDates) || !validDates(value.collectionUnavailableDates)
        || !validDates(value.deliveryNoticeUnavailableDates) || !validDates([value.noticeDate])
        || typeof value.noticeValidUntil !== "string" || !Number.isFinite(Date.parse(value.noticeValidUntil))) {
        throw new Error("Delivery and collection dates could not be loaded. Try again.")
    }
    return value
}

export function inferRangeChoice(range: DateRange | undefined, product: Product | null): RangeChoice | null {
    if (!range?.from || !range.to || !product) return null
    const start = range.from <= range.to ? range.from : range.to
    const end = range.from <= range.to ? range.to : range.from
    const inclusiveDays = dayDifference(start, end) + 1
    const hireDays = Math.max(0, inclusiveDays - (product.pricing?.model === "daily_weekly_inclusive" ? 0 : 2))
    const startDate = toDateOnly(start)
    if (inclusiveDays < minimumBookingDays(product) || hireDays < (product.pricing?.minimum_hire_days ?? 0)) return null
    const selection: BookingSelection = { type: "date_range", deliveryDate: startDate, collectionDate: toDateOnly(end) }
    const chargedDays = product.pricing ? billableHireDays(hireDays, product.pricing) : hireDays
    const label = `${chargedDays}-day hire`
    if (product.pricing?.model === "daily_weekly_inclusive") {
        const rateLabel = hireDays < 7 ? (product.pricing.billing_policy?.version === 2 ? "Short hire rate" : "Daily rate") : hireDays >= product.pricing.long_term_minimum_days! ? "Long hire weekly rate" : "Weekly rate"
        const charge = calculateHireCharge(hireDays, product.pricing)
        return { label, rateLabel, hireDays: chargedDays, windowDays: inclusiveDays, selection, pricePence: charge.unitPricePence, weeklyCapApplied: charge.weeklyCapApplied }
    }
    if (hireDays <= 1 && product.options.oneDay.enabled) {
        return { label, rateLabel: "1-day rate", hireDays, windowDays: inclusiveDays, selection, pricePence: product.options.oneDay.unitPricePence ?? 0 }
    }
    if (hireDays === 2 && product.options.twoDay.enabled) {
        return { label, rateLabel: "2-day rate", hireDays, windowDays: inclusiveDays, selection, pricePence: product.options.twoDay.unitPricePence ?? 0 }
    }
    if (hireDays >= 3 && product.options.weekly.enabled) {
        const weeklyPrice = product.options.weekly.unitPricePence
        const pricePence = hireDays <= 7
            ? weeklyPrice
            : calculateWeeklyHirePence(hireDays, weeklyPrice)
        return { label, rateLabel: hireDays <= 7 ? "7-day rate" : "7-day rate + pro-rata days", hireDays, windowDays: inclusiveDays, selection, pricePence }
    }
    return null
}

export function rangeError(range: DateRange | undefined, product: Product | null) {
    if (!range?.from) return "Choose the delivery date."
    if (!range.to) return "Now choose the collection date."
    if (inferRangeChoice(range, product)) return ""
    const days = dayDifference(range.from, range.to) + 1
    const minimumDays = minimumBookingDays(product)
    if (product?.productGroup === "dehumidifier" && days < 3) return MINIMUM_HIRE_MESSAGE
    if (minimumDays === 3 && days < minimumDays) return "Leave at least one full day between delivery and collection."
    if (minimumDays > 2 && days < minimumDays) return `Minimum hire is ${minimumDays} days, including delivery and collection.`
    if (days < 2) return "Collection must be at least one day after delivery."
    const hireDays = Math.max(0, days - (product?.pricing?.model === "daily_weekly_inclusive" ? 0 : 2))
    if (hireDays <= 1 && !product?.options.oneDay.enabled) return "Short hire is not available for this product."
    if (hireDays === 2 && !product?.options.twoDay.enabled) return "Two-day hire is not available for this product."
    return "Choose valid delivery and collection dates."
}

export function derivedDates(choice: RangeChoice | null) {
    if (!choice) return null
    return {
        hireStartDate: choice.selection.deliveryDate,
        hireEndDate: choice.selection.collectionDate,
        deliveryDate: choice.selection.deliveryDate,
        collectionDate: choice.selection.collectionDate,
    }
}
